import { FC, useCallback, useState } from 'react';
import { cx } from '@emotion/css';
import { useTranslation } from 'next-i18next';
import { FileError, FileRejection, useDropzone } from 'react-dropzone';
import { DSButton, DSDialog, DSHeading } from '@stihl-design-system/components';

import { getDesignSystemIcon } from '@phoenix/design-system/frontend/util/path.utils';

import { PhoenixIcon } from '../../phoenix-icon/phoenix-icon';
import { Button } from '../../button/button';
import { useDialog } from '../../modal/useDialog';
import { Spinner } from '../../spinner/spinner';
import { Label } from '../label/label';

import styles from './upload-field.module.scss';

export const mimeTypeMap = {
  gif: 'image/gif',
  png: 'image/png',
  jpeg: 'image/jpeg',
  webp: 'image/webp',
  heic: 'image/heic',
  pdf: 'application/pdf',
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  tiff: 'image/tiff',
  csv: 'text/csv',
};

const uploadIcon = getDesignSystemIcon('icon-stihl-upload.svg');
const crossIcon = getDesignSystemIcon('icon-cross-16.svg');
const circleXIcon = getDesignSystemIcon('icon-circle-x.svg');
const circleCheckIcon = getDesignSystemIcon('icon-circle-check.svg');
const pdfIcon = getDesignSystemIcon('icon-pdf-16.svg');
const docIcon = getDesignSystemIcon('icon-doc-16.svg');

export type AllowedFileType = keyof typeof mimeTypeMap;
export type FileUploadStatus = 'Added' | 'Uploading' | 'Uploaded' | 'UploadCompleted' | 'Rejected';

type UploadSuccessfulState = { state: Exclude<FileUploadStatus, 'Rejected'>; file: File; url?: string };
type UploadFailureState = { state: Extract<FileUploadStatus, 'Rejected'>; file: File; errors?: FileError[] };

export type FileState = UploadSuccessfulState | UploadFailureState;

type UploadFieldCommonProps = {
  allowedFileTypes?: AllowedFileType[];
  maxFiles?: number;
  maxSize?: number;
  label?: string;
  labelClassName?: string;
  permissions?: { read: boolean; write: boolean };
  'data-testid'?: string;
  containerClassName?: string;
  renderDeleteButton?: (file: FileState | FileRejection) => JSX.Element | null;
  renderFileDetails?: (file: FileState | FileRejection) => JSX.Element;
  localeRFC5646?: string;
};

// if the upload field is disabled, we do not support filesAdded or filesRemoved handlers,
// which leads to the second part of this union type where both are explicitly set to never
export type UploadFieldProps = UploadFieldCommonProps &
  (
    | {
        disabled?: boolean;
        onFilesAdded: (files: File[]) => void;
        onFileRemoved: (file: FileState) => void;
        fileStates: FileState[];
      }
    | {
        disabled: true;
        onFilesAdded?: never;
        onFileRemoved?: never;
        fileStates?: FileState[];
      }
  );

const uploadFieldClassName =
  'flex flex-col border border-dashed min-h-[12rem] items-center justify-center w-full gap-4 stihl-text-body-default-reg mt-2';
const uploadFieldHoverClassName = 'bg-secondary-light';

export const UploadField: FC<React.PropsWithChildren<UploadFieldProps>> = (props: UploadFieldProps) => {
  const { t } = useTranslation('shared');

  const {
    disabled,
    allowedFileTypes = undefined,
    maxFiles = 0,
    maxSize = 10240,
    onFilesAdded,
    label,
    labelClassName,
    permissions = { read: true, write: true },
    'data-testid': dataTestId,
    containerClassName = styles.uploadField,
    renderDeleteButton = () => renderIcon(crossIcon),
    renderFileDetails,
    localeRFC5646,
  } = props;
  const [className, setClassName] = useState(uploadFieldClassName);

  const onDrop = useCallback(
    (acceptedFiles: any) => {
      setClassName(uploadFieldClassName);
      if (onFilesAdded) {
        onFilesAdded(acceptedFiles);
      }
    },
    [onFilesAdded]
  );

  const defaultRenderFileDetails = (fileState: FileState | FileRejection) => (
    <div className="grid flex-1 grid-cols-[max-content,1fr,max-content] items-center gap-2">
      <div className="flex items-center">{renderFileIconByState(fileState)}</div>
      <div className="stihl-text-body-default-reg truncate">{fileState.file.name}</div>
      <div className="items-center justify-self-end stihl-text-body-sm-reg">{formatBytes(fileState.file.size, 1, localeRFC5646)}</div>
    </div>
  );

  const _renderFileDetails = renderFileDetails ?? defaultRenderFileDetails;

  const { fileRejections, getRootProps, getInputProps, open } = useDropzone({
    onDrop,
    noClick: true,
    noKeyboard: true,
    multiple: true,
    maxFiles,
    maxSize: maxSize * 1024,
    accept: allowedFileTypes?.map((type) => mimeTypeMap[type] || ''),
    onDragEnter: () => setClassName(cx(uploadFieldClassName, uploadFieldHoverClassName)),
    onDragLeave: () => setClassName(uploadFieldClassName),
  });

  const renderDisabledUploadField = () => {
    return (
      <div className={cx('flex w-full flex-col justify-center', containerClassName)} data-testid={dataTestId}>
        <div
          className={cx(
            styles.uploadArea,
            'flex min-h-[12rem] w-full flex-col items-center justify-center gap-4 border border-dashed border-secondary text-secondary stihl-text-body-default-reg'
          )}
        >
          <span>
            <svg className="h-4 w-4">
              <use xlinkHref={`${uploadIcon}#img`} />
            </svg>
          </span>
          <span className="hidden md:block">{t('shared_uploadField_dragAndDrop_text')}</span>
          <Button
            className="bg-primary-white stihl-text-interactive-button"
            btnType="grey_outline"
            disabled={true}
            data-testid="browseFiles"
          >
            {t('shared_uploadField_browseFiles')}
          </Button>
        </div>
        <UploadedFiles
          {...props}
          disabled={true}
          fileRejections={fileRejections}
          maxSize={maxSize}
          maxFiles={maxFiles}
          permissions={permissions}
          renderDeleteButton={renderDeleteButton}
          renderFileDetails={_renderFileDetails}
          localeRFC5646={localeRFC5646}
        />
        <div className="mt-6 text-center text-secondary stihl-text-body-xs-reg">
          {t('shared_uploadField_allowedFiles', {
            fileTypes: formatAllowedFileTypes(allowedFileTypes),
            maxSize: formatFilesize(maxSize),
          })}
        </div>
      </div>
    );
  };

  if (!permissions.read) {
    return null;
  }

  if (disabled || !permissions.write) {
    return renderDisabledUploadField();
  }

  const inputDataTestId = dataTestId ? `${dataTestId}_uploadField` : 'uploadField';

  return (
    <div className={cx('grid w-full', containerClassName)} data-testid={dataTestId}>
      <UploadedFiles
        {...props}
        fileRejections={fileRejections}
        maxSize={maxSize}
        maxFiles={maxFiles}
        permissions={permissions}
        renderDeleteButton={renderDeleteButton}
        renderFileDetails={_renderFileDetails}
      />
      {label && (
        <label
          className={cx('mt-2 flex flex-col stihl-text-body-default-reg', styles.label, labelClassName)}
          id={`${inputDataTestId}-label`}
        >
          <Label disabled={disabled} text={label} className={labelClassName} />
        </label>
      )}
      <button
        className={cx(styles.uploadArea, className, 'focus-outline')}
        type="button"
        {...getRootProps()}
        onClick={open}
        data-testid="browseFiles"
        aria-labelledby={`${inputDataTestId}-label ${inputDataTestId}-instructions`}
        aria-describedby={`${inputDataTestId}-allowedTypes`}
      >
        <input {...getInputProps()} data-testid={inputDataTestId} aria-label={t('shared_uploadField_aria_label') as string} />
        <PhoenixIcon icon={uploadIcon} />
        <span className="hidden tablet:block" id={`${inputDataTestId}-instructions`}>
          {t('shared_uploadField_dragAndDrop_text')}
        </span>
        <span className="stihl-text-interactive-button">{t('shared_uploadField_browseFiles')}</span>
      </button>
      <div
        className={cx(styles.allowedTypes, 'text-center text-secondary-dark stihl-text-body-xs-reg')}
        id={`${inputDataTestId}-allowedTypes`}
      >
        {t('shared_uploadField_allowedFiles', {
          fileTypes: formatAllowedFileTypes(allowedFileTypes),
          maxSize: formatFilesize(maxSize),
        })}
      </div>
    </div>
  );
};

type UploadedFilesCommonProps = {
  allowedFileTypes?: AllowedFileType[];
  maxSize: number;
  maxFiles: number;
  permissions: { read: boolean; write: boolean };
  fileRejections: FileRejection[];
  className?: string;
  renderDeleteButton: (file: FileState | FileRejection) => JSX.Element | null;
  renderFileDetails: (file: FileState | FileRejection) => JSX.Element;
  localeRFC5646?: string;
};

export type UploadedFilesProps = UploadedFilesCommonProps &
  (
    | {
        disabled?: boolean;
        onFileRemoved: (file: FileState) => void;
        fileStates: FileState[];
      }
    | {
        disabled: true;
        onFileRemoved?: never;
        fileStates?: FileState[];
      }
  );

const UploadedFiles = ({
  disabled,
  fileStates,
  onFileRemoved,
  allowedFileTypes,
  maxSize,
  maxFiles,
  fileRejections,
  permissions,
  renderDeleteButton,
  renderFileDetails,
  localeRFC5646,
}: UploadedFilesProps) => {
  const { t } = useTranslation('shared');

  const { dialogIsOpen, openDialog, closeDialog } = useDialog();
  const [currentFileState, setCurrentFileState] = useState<FileState>();

  const getErrorMessageParameters = (code: string): Record<string, unknown> => {
    if (code === 'file-invalid-type') {
      return {
        fileTypes: allowedFileTypes?.join(', '),
      };
    }

    if (code === 'too-many-files') {
      return {
        maxFiles,
      };
    }

    return {
      maxSize: formatBytes(maxSize * 1024, 1, localeRFC5646),
    };
  };

  const removeFile = (fileState: FileState) => {
    if (onFileRemoved) {
      onFileRemoved(fileState);
      setCurrentFileState(undefined);
    }
  };

  const renderDropzoneErrors = (errors) => {
    return (
      <div>
        <ul className="pl-6 stihl-text-body-sm-bold">
          {errors.map((e) => (
            <li key={e.code}>{t(`shared_uploadField_${e.code}`, getErrorMessageParameters(e.code))}</li>
          ))}
        </ul>
      </div>
    );
  };

  const onDeleteBtnClick = (fileState) => {
    setCurrentFileState(fileState);
    openDialog();
  };

  const renderFileItem = ({
    itemIndex,
    fileState,
    children,
    url,
    removeFileButton,
  }: {
    itemIndex: number;
    fileState: FileState | FileRejection;
    children: JSX.Element | null;
    url?: string;
    removeFileButton?: JSX.Element;
  }) => {
    const file = fileState.file;

    return (
      <li key={file.name + itemIndex} className={cx('mt-1 flex justify-between items-center p-6 pb-3', { 'pt-3': itemIndex > 0 })}>
        <a href={url} target="_blank" rel="noopener noreferrer" data-testid={`uploadedFile_${file.name}`}>
          {renderFileDetails(fileState)}
          <span className="sr-only">{t('shared_a11y_hint_opensInNewTab')}</span>
        </a>
        {removeFileButton && fileState && (
          <button
            className="focus-outline"
            type="button"
            onClick={() => onDeleteBtnClick(fileState)}
            data-testid={`removeFile_${fileState.file.name}`}
            aria-haspopup="dialog"
          >
            {removeFileButton}
          </button>
        )}
        {children}
      </li>
    );
  };

  return (
    <>
      <ul className={cx(styles.successfulFiles, 'mt-2')}>
        {fileStates &&
          fileStates.map((state, index) =>
            renderFileItem({
              itemIndex: index,
              fileState: state,
              children: disabled ? null : state.state === 'Rejected' ? renderDropzoneErrors(state.errors) : null,
              url: state.state !== 'Rejected' ? state.url : undefined,
              removeFileButton: disabled || !permissions.write ? undefined : renderDeleteButton(state) || undefined,
            })
          )}
      </ul>
      <ul className={styles.failedFiles}>
        {fileRejections.map((fileRejection, index) =>
          renderFileItem({
            itemIndex: index + (fileStates ? fileStates.length : 0),
            fileState: fileRejection,
            children: renderDropzoneErrors(fileRejection.errors),
          })
        )}
      </ul>
      <DSDialog
        isOpen={dialogIsOpen}
        onClose={closeDialog}
        aria-label={`${t('shared_uploadFile_removeAttachmentModalTitle')}${
          currentFileState?.file.name ? ` (${currentFileState?.file.name})` : ''
        }`}
        header={<DSHeading tag="h3">{t('shared_uploadFile_removeAttachmentModalTitle')}</DSHeading>}
        footer={
          <div className="flex gap-4">
            <DSButton
              type="button"
              onClick={() => {
                removeFile(currentFileState!);
                closeDialog();
              }}
              data-testid="confirm-attachment-delete-btn"
            >
              {t('shared_confirmModalButton')}
            </DSButton>
            <DSButton type="button" onClick={() => closeDialog()} variant="outline">
              {t('shared_cancel_button')}
            </DSButton>
          </div>
        }
      >
        <div className="stihl-text-body-default-reg">{t('shared_uploadFile_removeAttachmentModalBody')}</div>
      </DSDialog>
    </>
  );
};

const renderFileIconByState = (fileState: FileState | FileRejection) => {
  if (!Object.prototype.hasOwnProperty.call(fileState, 'state') || !fileState['state']) {
    return fileStateIconRenderer.Rejected;
  }

  const _fileState = fileState as FileState;

  if (_fileState.state === 'UploadCompleted') {
    return renderThumbnail(_fileState);
  }

  return fileStateIconRenderer[_fileState.state];
};

const renderThumbnail = (fileState: UploadSuccessfulState) => {
  switch (fileState.file.type) {
    case 'application/pdf':
      return renderIcon(pdfIcon);
    case 'application/msword':
    case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
      return renderIcon(docIcon);
    default:
      return <img className="h-6 justify-self-center object-contain" src={fileState.url} alt={fileState.file.name} />;
  }
};

const renderIcon = (icon: string, color?: string) => (
  <svg className={cx('h-4 w-4', color)}>
    <use xlinkHref={`${icon}#img`} />
  </svg>
);

export const fileStateIconRenderer = {
  Added: renderIcon(uploadIcon),
  Uploading: (
    <Spinner
      className="relative h-4 w-4 rounded-half border-[0.13rem] border-secondary-snow border-l-primary-black text-[0.625rem]"
      data-testid="uploadFileSpinner"
    />
  ),
  Uploaded: renderIcon(circleCheckIcon, 'text-green'),
  Rejected: renderIcon(circleXIcon, 'text-red'),
};

function formatFilesize(size: number): string {
  if (size >= 1024) {
    return `${Math.floor(size / 1024)}MB`;
  }

  return `${size}KB`;
}

function formatAllowedFileTypes(allowedFileTypes?: string[]): string {
  if (!allowedFileTypes) {
    return '';
  }

  return allowedFileTypes.map((t) => t.toUpperCase()).join(', ');
}

export function formatBytes(bytes: number, decimals = 1, localeRFC5646: string | undefined = undefined) {
  if (bytes === 0) return '0 b';

  const sizeMultiplicator = 1024;
  const digits = decimals < 0 ? 0 : decimals;
  const sizes = ['b', 'kb', 'mb'];
  const locale = localeRFC5646;

  const sizeIndex = Math.floor(Math.log(bytes) / Math.log(sizeMultiplicator));

  const formatter = new Intl.NumberFormat(locale, {
    minimumFractionDigits: 0,
    maximumFractionDigits: digits,
  });

  return formatter.format(bytes / Math.pow(sizeMultiplicator, sizeIndex)) + ' ' + sizes[sizeIndex];
}
